// ============================================================================
// listState — estado de TELA DE LISTA que sobrevive à navegação.
// ----------------------------------------------------------------------------
// Problema: ao abrir os detalhes de um projeto o React Router DESMONTA a tela
// de lista. Todo `useState` morre ali, então "Retornar" remontava a lista do
// zero — filtro aplicado sumia, paginação voltava para a página 1 e a rolagem
// ia para o topo.
//
// Solução: guardar esse estado em sessionStorage. Ele sobrevive à navegação
// (entrar nos detalhes e voltar) e morre quando a aba é fechada — diferente de
// usePersistentState (localStorage), que faria o filtro "grudar" para sempre,
// e o usuário voltaria no dia seguinte com a lista filtrada sem lembrar.
//
// NÃO há escopo por uid aqui (usePersistentState tem): sessionStorage já é
// isolado por aba, e a leitura precisa ser SÍNCRONA no primeiro render —
// esperar o auth resolver faria o filtro piscar vazio e descartar a página
// restaurada. Para não vazar filtros entre contas na mesma aba, o logout chama
// clearSessionState().
// ============================================================================
import { useState, useEffect, useRef, useCallback } from "react";

const PREFIX = "listState:";

// key falsy = não persiste (o hook vira um useState comum). Útil para telas
// que ainda não escolheram uma chave.
const read = (key, fallback) => {
  if (!key) return fallback;
  try {
    const raw = sessionStorage.getItem(PREFIX + key);
    if (raw == null) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const write = (key, value) => {
  if (!key) return;
  try {
    sessionStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    /* quota/serialização — ignora */
  }
};

// Apaga TODO o estado de tela guardado nesta aba. Chamado no logout.
export function clearSessionState() {
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith(PREFIX))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* ignora */
  }
}

// ----------------------------------------------------------------------------
// useSessionState — drop-in de useState persistido na sessão da aba.
//
//   const [page, setPage] = useSessionState("masterProjects_page", 1);
// ----------------------------------------------------------------------------
export function useSessionState(key, defaultValue) {
  const [value, setValue] = useState(() => read(key, defaultValue));

  // Recarrega se a chave mudar (raro; mantém o hook honesto).
  const keyRef = useRef(key);
  useEffect(() => {
    if (keyRef.current === key) return;
    keyRef.current = key;
    setValue(read(key, defaultValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Persiste nas mudanças (pula o primeiro render para não gravar o default).
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    write(key, value);
  }, [key, value]);

  return [value, setValue];
}

// ----------------------------------------------------------------------------
// usePageGuard — substitui o `useEffect(() => setPage(1), [filtros])` das telas.
//
// Faz as DUAS coisas que a paginação restaurada precisa:
//   1. volta à 1ª página quando os filtros mudam — MAS NÃO na montagem, senão
//      a página restaurada da sessão seria descartada logo ao voltar;
//   2. recua a página se a lista encolheu (dados mudaram enquanto o usuário
//      estava nos detalhes), evitando cair numa página vazia.
//
//   usePageGuard({ page, setPage, totalPages, resetOn: [std.filters, perPage] })
//
// O reset compara os VALORES de `resetOn` entre renders em vez de usá-los como
// lista de deps. É de propósito: com deps, o StrictMode (que remonta o efeito
// em desenvolvimento) dispararia o reset logo na montagem e jogaria fora a
// página restaurada — justamente o que este hook existe para evitar.
// ----------------------------------------------------------------------------
export function usePageGuard({ page, setPage, totalPages, resetOn = [] }) {
  const prev = useRef(null);
  useEffect(() => {
    if (prev.current === null) {
      prev.current = resetOn;
      return;
    }
    const changed =
      resetOn.length !== prev.current.length ||
      resetOn.some((v, i) => !Object.is(v, prev.current[i]));
    prev.current = resetOn;
    if (changed) setPage(1);
  });

  useEffect(() => {
    if (totalPages > 0 && page > totalPages) setPage(totalPages);
  }, [page, totalPages, setPage]);
}

// ----------------------------------------------------------------------------
// useScrollRestore — devolve a rolagem da página ao voltar dos detalhes.
//
// A lista carrega do Firestore de forma assíncrona, então na montagem a página
// ainda é curta demais para rolar até a posição salva. Por isso tentamos por
// alguns segundos (a cada frame), parando assim que a posição é alcançada — ou
// assim que o usuário mexe na rolagem, para nunca puxar a página debaixo dele.
// ----------------------------------------------------------------------------
export function useScrollRestore(key, { enabled = true } = {}) {
  useEffect(() => {
    if (!enabled || !key || typeof window === "undefined") return undefined;

    const storeKey = `${key}_scroll`;
    const target = Number(read(storeKey, 0)) || 0;

    let restoring = target > 0;
    let raf = 0;
    let latest = target;
    const deadline = Date.now() + 4000;

    const stopRestoring = () => {
      if (!restoring) return;
      restoring = false;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      latest = window.scrollY;
    };

    const step = () => {
      if (!restoring) return;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      window.scrollTo(0, Math.min(target, Math.max(0, max)));
      if (max >= target || Date.now() > deadline) {
        restoring = false;
        raf = 0;
        latest = window.scrollY;
        return;
      }
      raf = requestAnimationFrame(step);
    };

    if (restoring) {
      raf = requestAnimationFrame(step);
      // Qualquer intenção de rolar do usuário cancela a restauração.
      window.addEventListener("wheel", stopRestoring, { passive: true });
      window.addEventListener("touchstart", stopRestoring, { passive: true });
      window.addEventListener("keydown", stopRestoring);
    }

    // Enquanto restauramos, o scrollTo dispara "scroll" — ignoramos, senão
    // gravaríamos a posição intermediária (página ainda curta) por cima da boa.
    const onScroll = () => {
      if (restoring) return;
      latest = window.scrollY;
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    const persist = () => write(storeKey, latest);
    window.addEventListener("pagehide", persist);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("wheel", stopRestoring);
      window.removeEventListener("touchstart", stopRestoring);
      window.removeEventListener("keydown", stopRestoring);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", persist);
      persist();
    };
  }, [key, enabled]);
}

// ----------------------------------------------------------------------------
// useScrollTop — contrapartida do useScrollRestore: telas de DETALHE começam no
// topo. Sem isto, sair de uma lista rolada abre o detalhe no meio da página.
// ----------------------------------------------------------------------------
export function useScrollTop(enabled = true) {
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    window.scrollTo(0, 0);
  }, [enabled]);
}

// ----------------------------------------------------------------------------
// useSessionFilter — açúcar para os filtros ESPECÍFICOS de cada tela (os que
// não vêm de useStandardFilters), com um `clear` pronto.
// ----------------------------------------------------------------------------
export function useSessionFilter(key, defaultValue) {
  const [value, setValue] = useSessionState(key, defaultValue);
  const clear = useCallback(() => setValue(defaultValue), [setValue, defaultValue]);
  return [value, setValue, clear];
}
